/**
 * Break pricing math, on the client.
 *
 * The same integer-cents formula the backend uses, deliberately duplicated
 * rather than imported: the SPA has no `file:` dependency on the backend, and
 * the host needs the recommended price to update on every keystroke while they
 * drag a multiplier — a round trip per character is not that.
 *
 * The **server** is still the authority. It recomputes on create and on every
 * reprice, so a client that drifted from this file would show a stale number and
 * then be corrected, never overcharge anyone. Keep the two in step anyway:
 * `backend/src/core/libs/live/calculator.ts` is the original and carries the
 * tests.
 *
 * No floating point money anywhere. `formatCents` is for display only.
 */

export const MULTIPLIER_PRESETS = [150, 200, 250, 300] as const;

/** Slot counts that come with a league. TCG and player breaks are custom. */
export const LEAGUE_SLOT_COUNTS = {
  NBA: 30,
  MLB: 30,
  NFL: 32,
  NHL: 32,
} as const;

export function formatCents(cents: number, currency = "USD"): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

/** $45 rather than $45.00 when it is a round number. For dense grids. */
export function formatCentsCompact(cents: number): string {
  return cents % 100 === 0 ? `$${(cents / 100).toLocaleString("en-US")}` : formatCents(cents);
}

/** Multiplier stored as percent (200 = 2x) back to a display string. */
export function formatMultiplier(pct: number): string {
  const x = pct / 100;
  return `${Number.isInteger(x) ? x : x.toFixed(2)}x`;
}

/** Dollars typed into a field, to cents. Empty and nonsense both give 0. */
export function dollarsToCents(input: string): number {
  const cleaned = input.trim().replace(/[$,\s]/g, "");
  if (cleaned === "") return 0;
  const value = Number(cleaned);
  if (!Number.isFinite(value) || value < 0) return 0;
  return Math.round(value * 100);
}

export type BreakPricing = {
  recommendedPriceCents: number;
  projectedRevenueCents: number;
  projectedProfitCents: number;
  breakEvenPriceCents: number;
  marginPct: number;
  profitable: boolean;
};

function roundUpTo(valueCents: number, incrementCents: number): number {
  if (incrementCents <= 1) return Math.ceil(valueCents);
  return Math.ceil(valueCents / incrementCents) * incrementCents;
}

/**
 * price_per_slot = (market value x multiplier) / slot_count, rounded UP to the
 * dollar.
 *
 * Up rather than to-nearest because hosts price in dollars and rounding down
 * would quietly miss the target multiplier they just chose.
 */
export function calculateBreak(input: {
  marketValueCents: number;
  costCents: number;
  targetMultiplierPct: number;
  slotCount: number;
  roundToCents?: number;
}): BreakPricing {
  const { marketValueCents, costCents, targetMultiplierPct, slotCount } = input;
  const roundToCents = input.roundToCents ?? 100;

  if (slotCount < 1) {
    return {
      recommendedPriceCents: 0,
      projectedRevenueCents: 0,
      projectedProfitCents: -costCents,
      breakEvenPriceCents: 0,
      marginPct: 0,
      profitable: false,
    };
  }

  const targetRevenueCents = Math.round((marketValueCents * targetMultiplierPct) / 100);
  const exactPriceCents = targetRevenueCents / slotCount;
  const recommendedPriceCents = Math.max(roundUpTo(exactPriceCents, roundToCents), roundToCents);

  const projectedRevenueCents = recommendedPriceCents * slotCount;
  const projectedProfitCents = projectedRevenueCents - costCents;

  return {
    recommendedPriceCents,
    projectedRevenueCents,
    projectedProfitCents,
    breakEvenPriceCents: Math.ceil(costCents / slotCount),
    marginPct: projectedRevenueCents === 0 ? 0 : Math.round((projectedProfitCents / projectedRevenueCents) * 1000) / 10,
    profitable: projectedProfitCents > 0,
  };
}

/** Said after a cancel when Stripe refused some refunds: the break is cancelled, the money is not back. */
export function refundsFailedText(count: number): string {
  return `The break is cancelled, but ${count === 1 ? "1 refund" : `${count} refunds`} did not go through. Refund ${count === 1 ? "that buyer" : "those buyers"} in your Stripe dashboard.`;
}

const OVER = new Set(["ENDED", "CANCELLED"]);

/** Terminal. No chat, no sales, no reveals, no stream. */
export function streamIsOver(status: string): boolean {
  return OVER.has(status);
}

/**
 * LIVE, but no video is arriving: the host has stepped away.
 *
 * A stream deliberately stays LIVE across a dropped phone, because ending it on
 * every blip would kill a break mid-reveal. So "is the session open" and "is
 * there a picture" are different questions, and conflating them is what puts a
 * pulsing red LIVE badge over a black rectangle. The server already computes
 * this — `streamPaused` on the stream — so read that rather than re-deriving it.
 */
export function liveBadge(stream: { status: string; streamPaused: boolean }): {
  label: string;
  cls: string;
} {
  if (stream.status === "LIVE" && stream.streamPaused) {
    return { label: "Back shortly", cls: "badge-warn" };
  }
  if (stream.status === "LIVE") return { label: "Live", cls: "badge-danger" };
  if (stream.status === "SCHEDULED") return { label: "Upcoming", cls: "badge-accent" };
  if (stream.status === "ENDED") return { label: "Ended", cls: "badge-neutral" };
  return { label: "Cancelled", cls: "badge-neutral" };
}

const BREAK_BADGES: Record<string, { label: string; cls: string }> = {
  DRAFT: { label: "Draft", cls: "badge-neutral" },
  ON_SALE: { label: "On sale", cls: "badge-accent" },
  LOCKED: { label: "Locked", cls: "badge-warn" },
  RANDOMIZED: { label: "Teams drawn", cls: "badge-warn" },
  BREAKING: { label: "Ripping", cls: "badge-danger" },
  COMPLETED: { label: "Done", cls: "badge-neutral" },
  CANCELLED: { label: "Cancelled", cls: "badge-neutral" },
};

export function breakBadge(status: string): { label: string; cls: string } {
  return BREAK_BADGES[status] ?? { label: status, cls: "badge-neutral" };
}

/** A clock: "12:04", or "1:12:40" past the hour. */
export function formatClock(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** How long, said: "1h 12m", "45m". */
export function formatSpan(ms: number): string {
  const minutes = Math.max(0, Math.round(ms / 60_000));
  const h = Math.floor(minutes / 60);
  return h > 0 ? `${h}h ${minutes % 60}m` : `${minutes}m`;
}
